import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { listEditions, updateEvent } from "@/api/events";

/**
 * Editions: link this event to last year's (same host, earlier date). The
 * server never guesses a series from a title; this is the organiser's act.
 */
export function EventEditionsPanel({ eventId }: { eventId: string }) {
  const qc = useQueryClient();
  const [previousId, setPreviousId] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const { data } = useQuery({ queryKey: ["editions", eventId], queryFn: () => listEditions(eventId) });
  const editions = data?.data.editions ?? [];

  async function link(previousEventId: string | null) {
    setMsg(null);
    try {
      await updateEvent(eventId, { previousEventId });
      setMsg(previousEventId ? "Linked to the previous edition." : "Link removed.");
      setPreviousId("");
      await qc.invalidateQueries({ queryKey: ["editions", eventId] });
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "That did not work");
    }
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-gray-700">Editions ({editions.length})</h3>
      <ul className="text-xs text-gray-600 space-y-1">
        {editions.map((e) => (
          <li key={e.id}>
            {new Date(e.eventDate).toLocaleDateString()} · {e.title}
            {e.editionNumber ? ` · edition ${e.editionNumber}` : ""} · {e.wentCount} went
            {e.id === eventId ? " (this one)" : ""}
          </li>
        ))}
      </ul>
      <div className="flex gap-2 flex-wrap">
        <input
          className="input w-80"
          placeholder="Previous edition's event id (same host, earlier date)"
          value={previousId}
          onChange={(e) => setPreviousId(e.target.value)}
        />
        <button className="px-3 py-1 rounded border border-gray-300 text-sm" disabled={!previousId.trim()} onClick={() => void link(previousId.trim())}>
          Link
        </button>
        <button className="px-3 py-1 rounded border border-gray-300 text-sm" onClick={() => void link(null)}>
          Remove link
        </button>
      </div>
      {msg && <p className="text-xs text-gray-600">{msg}</p>}
    </div>
  );
}
